import React, { forwardRef, useMemo } from 'react'
import ReportAccordion from './report_document/ReportAccordion'
import ReportSubSection from './report_document/ReportSubSection'
import AIReportSubSection from './report_document/AIReportSubSection'
import GraphContainer from '../../../ui_reusable/GraphContainer'
import AIEditableContainerWithTitle from '../../../ui_reusable/AIEditableContainerWithTitle'
import { AIHStack, HStack } from './styles/ReportDocument.styles'
import { useNavigate } from 'react-router-dom'
import { SmallStandardButton } from '../../../ui_reusable/styles/Button.styles'
import { useAuth } from '../../../../context/AuthContext'
import {
  insertCustomElements,
  isCustomJSX,
  getCustomJSXConfig,
  type ComponentRegistry,
  type InjectionConfig,
  type ExtendedReportDocument,
} from './utils/injection_system/InsertCustomElements'
import type { PlotlyContent } from '../../../../api/generated/models'
// Import the old types for compatibility

import { AITextType, type BaseSlice } from './stores/baseReportStore'

/**
 * Component registry getter function type
 */
type GetComponentByType = (type: string) => React.ComponentType<any> | null

// Type for a store that implements BaseSlice
type BaseStoreHook<T extends BaseSlice> = (selector: (state: T) => any) => any

/**
 * Props for ReportDocument component
 */
interface ReportDocumentProps {
  isNewlyGeneratedReport?: boolean
  injectionConfig?: InjectionConfig
  componentRegistry?: ComponentRegistry
  reportTitle?: string
  store?: BaseStoreHook<any>
}

const ReportDocument = forwardRef<HTMLDivElement, ReportDocumentProps>(
  (
    {
      isNewlyGeneratedReport,
      injectionConfig,
      componentRegistry,
      reportTitle,
      store,
    },
    ref,
  ) => {
    const { isAdmin } = useAuth()
    const navigate = useNavigate()

    // Get values using the passed store or fallback to defaults
    const hasAIPlus = store
      ? store((state: BaseSlice) => state.getAIPlus())
      : false
    const AISettings = store
      ? store((state: BaseSlice) => state.getAISettings())
      : undefined
    const aiPrompts = store
      ? store((state: BaseSlice) => state.getAIPrompts())
      : undefined
    const reportDocument = store
      ? store((state: BaseSlice) => state.getReportDocument())
      : undefined
    /**
     * Helper function to get component by type from the passed component registry
     *
     * This function maps string identifiers to actual React components using the
     * registry passed down from the parent component. This approach allows any
     * report type to use this ReportDocument with their own component registry.
     *
     * @param type - The component type string (e.g., 'scenario-numbers')
     * @returns The component or null if not found
     */
    const getComponentByType: GetComponentByType = (type: string) => {
      if (!componentRegistry) {
        console.warn(
          'Component registry not provided, custom components will not work',
        )
        return null
      }

      const Component = componentRegistry[type]
      if (!Component) {
        console.warn(`Custom component type "${type}" not found in registry`)
        return null
      }
      return Component
    }

    /**
     * VERSATILE CUSTOM INJECTION SYSTEM INTEGRATION
     *
     * This reusable component supports dynamic injection of custom JSX components
     * at runtime using a configurable injection system. The system works by:
     *
     * 1. Receiving injection configuration and component registry from any parent report page
     * 2. Processing the original reportDocument data through insertCustomElements() with the config and registry
     * 3. Detecting custom elements using the is_custom flag
     * 4. Dynamically resolving and rendering custom components using the provided registry
     * 5. Using the provided reportTitle to customize the report header
     *
     * WHY THIS VERSATILE APPROACH MATTERS:
     * - Any report type can use this component with their own injection configuration
     * - Each report type can have its own custom components and registry
     * - Each report type can specify its own title (Impact Report, Scenario Report, etc.)
     * - Backend defines structure, frontend defines custom positioning per report type
     * - Maintains clean separation between core logic and custom features
     * - Configuration and registry are passed down from parent, making the system reusable
     * - Co-location of config and registry improves maintainability per report type
     * - Backward compatibility when injection config/registry not provided
     *
     * The enhancedReportDocument contains the original plots plus any injected custom elements
     * based on the provided injection configuration and available components.
     */
    // Apply custom element injections to the reportDocument
    const enhancedReportDocument: ExtendedReportDocument = useMemo(() => {
      // If no reportDocument, return empty structure
      if (!reportDocument) {
        return { plots: [] } as ExtendedReportDocument
      }
      // If no injection config or registry provided, return reportDocument as-is for backward compatibility
      if (!injectionConfig || !componentRegistry) {
        return reportDocument as ExtendedReportDocument
      }
      return insertCustomElements(
        reportDocument,
        injectionConfig,
        componentRegistry,
      )
    }, [reportDocument, injectionConfig, componentRegistry])

    return (
      <>
        <ReportAccordion
          id={'reportAccordion'}
          defaultActiveKey={['reportDocument']}
        >
          <ReportAccordion.Panel
            eventKey="reportDocument"
            header={reportTitle || 'Report'}
            helpText="This is a summary of the model and its performance."
          >
            <ReportSubSection
              title="Executive insights and conclusions"
              overviewText="Analysis summary and recommendations."
            >
              {isAdmin && (
                <div style={{ float: 'right' }}>
                  <SmallStandardButton
                    onClick={() =>
                      navigate('/prompts', { state: { data: aiPrompts } })
                    }
                  >
                    Check AI Prompts
                  </SmallStandardButton>
                </div>
              )}

              {!hasAIPlus && (
                <AIHStack>
                  <AIEditableContainerWithTitle
                    isNewlyGeneratedReport={isNewlyGeneratedReport}
                    key="key_insights"
                    title="Key insights"
                    textSrcType={AITextType.KEY_INSIGHTS}
                    prompt={`Based on the descriptions of data plots, extract key insights within 5 points:\n\n${(enhancedReportDocument.plots || []).flatMap((elem) => (elem.plots || []).map((crntPlotPackage) => crntPlotPackage.ai_description ?? '').filter((desc) => desc)).join('\n')}`}
                    store={store}
                  />
                  <AIEditableContainerWithTitle
                    isNewlyGeneratedReport={isNewlyGeneratedReport}
                    key="conclusion"
                    title="Conclusions"
                    textSrcType={AITextType.CONCLUSION}
                    prompt={`Based on the descriptions of data plots, provide a clear and concise conclusion within 500 words:\n\n${(enhancedReportDocument.plots || []).flatMap((elem) => (elem.plots || []).map((crntPlotPackage) => crntPlotPackage.ai_description ?? '').filter((desc) => desc)).join('\n')}`}
                    store={store}
                  />
                </AIHStack>
              )}
            </ReportSubSection>
            {hasAIPlus && (
              <AIReportSubSection title="Your exclusive insights">
                <AIHStack>
                  <AIEditableContainerWithTitle
                    isNewlyGeneratedReport={isNewlyGeneratedReport}
                    key="key_insights_plus"
                    title={`Insights`}
                    textSrcType={AITextType.KEY_INSIGHTS_PLUS}
                    prompt={`Based on the descriptions of data plots, extract key insights within 5 points:\n\n${(enhancedReportDocument.plots || []).flatMap((elem) => (elem.plots || []).map((crntPlotPackage) => crntPlotPackage.ai_plus_description ?? '').filter((desc) => desc)).join('\n')}\n\nAdditional Context: ${JSON.stringify(AISettings)}`}
                    store={store}
                  />
                  <AIEditableContainerWithTitle
                    isNewlyGeneratedReport={isNewlyGeneratedReport}
                    key="conclusion_plus"
                    title={`Conclusions`}
                    textSrcType={AITextType.CONCLUSION_PLUS}
                    prompt={`Based on the descriptions of data plots, provide a clear and concise conclusion within 500 words:\n\n${(enhancedReportDocument.plots || []).flatMap((elem) => (elem.plots || []).map((crntPlotPackage) => crntPlotPackage.ai_plus_description ?? '').filter((desc) => desc)).join('\n')}\n\nAdditional Context: ${JSON.stringify(AISettings)}`}
                    store={store}
                  />
                </AIHStack>
              </AIReportSubSection>
            )}

            {enhancedReportDocument?.plots?.map((plotGroup, i) => {
              /**
               * CUSTOM SUBSECTION DETECTION
               *
               * We check if this is a custom subsection using the is_custom flag.
               * Custom subsections bypass the normal plot rendering logic and
               * directly render the custom component based on the injection configuration.
               *
               * This versatile approach allows for:
               * - Complete custom layouts for any report type
               * - Custom data structures defined in any report's config
               * - Interactive components that don't fit the plot paradigm
               * - Report-specific customizations without affecting other report types
               * - Reusable ReportDocument component across multiple report types
               */
              // Check if this is a custom subsection
              if (plotGroup.is_custom) {
                // This is a custom subsection - render the custom component directly
                const customConfig = plotGroup.plots?.[0]?.plot
                  ? getCustomJSXConfig(plotGroup.plots[0].plot)
                  : null
                const CustomComponent = getComponentByType(
                  customConfig?.type || '',
                )

                if (CustomComponent && customConfig) {
                  return (
                    <ReportSubSection
                      key={`custom-subsection-${i}`}
                      title={plotGroup.title || ''}
                      overviewText={plotGroup.description}
                    >
                      <CustomComponent
                        plotGroup={plotGroup}
                        plotPackage={plotGroup.plots?.[0]}
                        index={i}
                        store={store}
                      />
                    </ReportSubSection>
                  )
                } else {
                  console.error(
                    `Custom component type "${customConfig?.type}" not found`,
                  )
                  return null
                }
              }

              /**
               * VERSATILE SUBSECTION WITH CUSTOM PLOT SUPPORT
               *
               * For regular subsections, we iterate through plots and check
               * each one for the is_custom flag. This allows mixing regular
               * plots with custom components within the same subsection.
               *
               * The injection configuration determines which custom components
               * are available and where they should be positioned.
               *
               * KEY DECISIONS:
               * - Custom components don't get refs (they might not support them)
               * - Custom components receive both their config data and full report context
               * - Error handling prevents crashes from missing components
               * - Any report type can use this with different custom components available
               * - Reusable across all report types while maintaining customization
               */
              // Regular subsection with plots
              return (
                <ReportSubSection
                  key={i + 1}
                  title={plotGroup.title || ''}
                  overviewText={plotGroup.description}
                >
                  <HStack>
                    {(plotGroup.plots || []).map((plotPackage, index) => {
                      if (plotPackage.plot) {
                        const plot = plotPackage.plot

                        /**
                         * VERSATILE CUSTOM PLOT DETECTION AND RENDERING
                         *
                         * Each plot is checked for the is_custom flag.
                         * Custom plots are rendered using their registered
                         * component as defined in the injection configuration,
                         * while regular plots use GraphContainer.
                         *
                         * This allows for plot-level customization within
                         * existing subsections, with different configurations
                         * and components for each report type using this ReportDocument.
                         */
                        // Check if this is a custom JSX element
                        if (isCustomJSX(plot)) {
                          const customConfig = getCustomJSXConfig(plot)
                          const CustomComponent = getComponentByType(
                            customConfig?.type || '',
                          )

                          if (CustomComponent && customConfig) {
                            return (
                              <CustomComponent
                                key={`custom-plot-${index}`}
                                plotGroup={plotGroup}
                                plotPackage={plotPackage}
                                index={index}
                                store={store}
                              />
                            )
                          } else {
                            console.error(
                              `Custom component type "${customConfig?.type}" not found`,
                            )
                            return null
                          }
                        } else {
                          // Regular plot rendering - cast to base PlotlyContent type for compatibility
                          const regularPlot = plot as PlotlyContent
                          return (
                            <div key={index + 1}>
                              <GraphContainer
                                isNewlyGeneratedReport={isNewlyGeneratedReport}
                                title={plotPackage.title || 'Untitled Plot'}
                                hasAIPlus={hasAIPlus}
                                AISettings={AISettings}
                                helpText={plotPackage.description}
                                loading={!regularPlot}
                                size={'small'}
                                wide={plotGroup.flex === 'row'}
                                data={regularPlot?.data as any}
                                downloadLink={(regularPlot as any)?.download_data}
                                layout={regularPlot?.layout as any}
                                plotGroupId={plotGroup?.id}
                                plotPackageId={plotPackage?.id}
                                store={store}
                              />
                            </div>
                          )
                        }
                      }
                      return null
                    })}
                  </HStack>
                </ReportSubSection>
              )
            })}
          </ReportAccordion.Panel>
        </ReportAccordion>
      </>
    )
  },
)

ReportDocument.displayName = 'ReportDocument'

export default ReportDocument
